import React, { useState, useContext, useRef } from 'react';

import ModalWrapper from  '@/components/modals/ModalWrapper'
import SearchUsersOrGroups from  '@/components/elements/LockerManagement/SearchUsersOrGroups';

import { LockerManagementFileUploadContext, SearchType } from '@/context/LockerManagementFileUploadContext';
import { PlannerSettingsContext } from '@/context/PlannerSettingsContext';
    
import { useEffect } from 'react';  

interface SelectUsersOrUserGroupsModalProps {
    show: boolean,
    className: string,
    onClose: (val: boolean) => void
    defaultTab: ActiveTabs
    setDefaultTab: (val: ActiveTabs) => void,
    planner?:boolean,
    disabledTabs?:ActiveTabs[],
    filterParams?:Object
}

export enum ActiveTabs {
    USERS = 'Search Users',
    GROUPS = 'Search Groups',
    SELECTEDUSERS = 'Selected Users',
    SELECTEDGROUPS = 'Selected Groups',
}

export interface IGroupsAndUsersContext {
    selectedUsers: any[],
    selectedGroups: any[],
    setSelectedUsers: (data: any[]) => void,
    setSelectedGroups: (data: any[]) => void
}

const SelectUsersOrUserGroupsModal = ({show, className, onClose, defaultTab, planner, disabledTabs, filterParams }: SelectUsersOrUserGroupsModalProps) => {
    const [activeTab, setActiveTab] = useState<ActiveTabs>(defaultTab ? defaultTab: ActiveTabs.USERS)

    const {selectedGroups, selectedUsers, setSelectedGroups, setSelectedUsers} = useContext(getContext());

    const [modalSelectedUsers, setModalSelectedUsers] = useState<any[]>(selectedUsers);
    const [modalSelectedGroups, setModalSelectedGroup] = useState<any[]>(selectedGroups);

    const selectedUsersBadge = useRef<HTMLSpanElement>();
    const selectedGroupsBadge = useRef<HTMLSpanElement>();

    const noOfGroups = modalSelectedGroups.length
    const noOfUsers = modalSelectedUsers.length

    useEffect(()=> {
        if(!show) {
            resetSelected();
        }
    },[show]);

    useEffect(()=> {
        resetSelected()
    },[selectedGroups, selectedUsers])

    useEffect(() => {
        triggerIncrementAnimation(selectedGroupsBadge)
    },[noOfGroups])
 
    useEffect(() => {
        triggerIncrementAnimation(selectedUsersBadge)
    },[noOfUsers])
    
    useEffect(()=>{
        setActiveTab(defaultTab)
    },[defaultTab])

    const stateMap = {
        [SearchType.GROUP]: {state: modalSelectedGroups, setState: setModalSelectedGroup},
        [SearchType.USER]: {state: modalSelectedUsers, setState: setModalSelectedUsers},
    }

    function getContext(){

        // Forcing these two interfaces into one, only four fields are used in both
        if (planner){
            return PlannerSettingsContext as unknown as  React.Context<IGroupsAndUsersContext>
        }

        return LockerManagementFileUploadContext as unknown as React.Context<IGroupsAndUsersContext>
    }

    function addToSelected(type:SearchType, data: any) {
        const {state, setState} = stateMap[type]
        setState([...state, data])
    }

    function removeFromSelected(type:SearchType, data: any) {
        const {state, setState} = stateMap[type];
        if (type === SearchType.USER) {
            setState(state.filter(el => el.id !== data.id))
        }
        else {
            setState(state.filter(el => el.groupName !== data.groupName))
        }
       
    }
    
    function resetSelected() {
        setModalSelectedGroup(selectedGroups);
        setModalSelectedUsers(selectedUsers);
    }
    
    function triggerIncrementAnimation(ref) {
        if(ref && ref.current) {
            ref.current.classList.add('animate')
            setTimeout(()=> {
                if(ref && ref.current) {
                    ref.current.classList.remove('animate');
                } 
            }, 600)
        }
    }

    function onConfirm() {
        setSelectedGroups(modalSelectedGroups);
        setSelectedUsers(modalSelectedUsers);
        onClose(false);
    }

    function isValid(){
        return modalSelectedGroups === selectedGroups && modalSelectedUsers === selectedUsers;
    }

    const NavLink = React.forwardRef(({tab, badgeNumber=0, disabledTabs}:{tab: ActiveTabs, badgeNumber?: number, disabledTabs?:ActiveTabs[]}, ref:React.Ref<HTMLSpanElement>) => {
      
        function isDisabled(){
            if (disabledTabs && disabledTabs.find((tabs) => tabs === tab)){
                return true
            }

            return ([ActiveTabs.SELECTEDGROUPS, ActiveTabs.SELECTEDUSERS].includes(tab) && badgeNumber === 0)
        }

        const isActive = activeTab === tab;

        return (
            <li className='modal-nav-item'>
                <button
                    className={`modal-nav-button ${isActive ? 'active': ''}`}
                    onClick={() => setActiveTab(tab)}
                    disabled={isDisabled()}
                >
                    {tab}
                </button>
            
                {!!badgeNumber && !isDisabled() && <span ref={ref} id={tab.replace(" ", "-")} className="modal-nav-badge">{badgeNumber}</span>}
            </li>
        )
    });
    
    return (
        <ModalWrapper 
            className={`standard-mini-modal${className ? ' ' + className : ''}`}
            isOpen={show}
            escClose={false}
            onRequestClose={onClose}>

        <div className="standard-mini-modal-wrapper">
            <h3 className="standard-mini-modal-title">Select User Groups/Users</h3>
            <div className="standard-mini-modal-content narrow-scroll-bar">
                <nav className='modal-nav'>
                    <ul>
                        <NavLink tab={ActiveTabs.USERS} disabledTabs={disabledTabs} />
                        <NavLink tab={ActiveTabs.GROUPS} disabledTabs={disabledTabs} />
                        <NavLink tab={ActiveTabs.SELECTEDUSERS} disabledTabs={disabledTabs} badgeNumber={noOfUsers} ref={selectedUsersBadge} />
                        <NavLink tab={ActiveTabs.SELECTEDGROUPS} disabledTabs={disabledTabs} badgeNumber={noOfGroups} ref={selectedGroupsBadge}/>          
                    </ul>
                </nav>
                <SearchUsersOrGroups
                    activeTab={activeTab}
                    addToSelected={addToSelected}
                    removeFromSelected={removeFromSelected}
                    selectedGroups={modalSelectedGroups}
                    selectedUsers={modalSelectedUsers}
                    planner={planner}
                    filterParams={filterParams}
                />
            </div>
            <div className="standard-mini-modal-button-wrapper">
                <button className="cancel-button" onClick={() => onClose(false)}>
                    Cancel
                </button>
                <button className="confirm-button" onClick={onConfirm} disabled={isValid()}>
                    Confirm
                </button>
            </div>
            
        </div> 
    </ModalWrapper>
    )
}

export default SelectUsersOrUserGroupsModal;